import * as XLSX from 'xlsx-js-style';
import { firmToday } from './dateRange';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Every export is stamped with today's firm date before the extension:
 * 'Late_Accounts_Report.xlsx' → 'Late_Accounts_Report_06 Oct 2026.xlsx'.
 * Back-end twin: datedFileName() in server/utils/firmDate.js.
 */
export function datedFilename(filename) {
  const [y, m, d] = firmToday().split('-');
  const stamp = `${d} ${MONTHS[Number(m) - 1]} ${y}`;
  const dot = filename.lastIndexOf('.');
  return dot > 0
    ? `${filename.slice(0, dot)}_${stamp}${filename.slice(dot)}`
    : `${filename}_${stamp}`;
}

// ── 全站共用的工作表樣式 ──────────────────────────────────────────────────
// 每份匯出都經過 buildSheet：表頭粗體＋底色、細框線、欄寬依內容、表頭篩選；
// 日期與數字轉成 Excel 自己的格式（可排序、可加總），不是畫面上的字串。
//   日期：呼叫端給的是畫面格式（'31 March 2024'／'31 Mar 2024'／ISO
//         'YYYY-MM-DD'／'31-Mar-2024 13:36'），這裡認出來轉成日期儲存格。
//   數字：同一欄取最多兩位小數，整欄同一種位數；時數／比率／平均（表頭
//         判斷）至少一位小數；年份／Rank／代碼類欄位不加千分位。

const BORDER = { style: 'thin', color: { rgb: 'BFBFBF' } };
const BORDERS = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
const HEADER_STYLE = {
  font: { bold: true, color: { rgb: '1F2937' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'D9E1F2' } },
  alignment: { vertical: 'center', wrapText: true },
  border: BORDERS,
};
const BODY_ALIGN = { vertical: 'top', wrapText: true };
const DATE_FMT = 'dd mmm yyyy';
const DATETIME_FMT = 'dd mmm yyyy hh:mm';
const MAX_WIDTH = 50;
const MIN_WIDTH = 8;
const HEADER_MAX_WIDTH = 24;

const MONTH_INDEX = {};
['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
  .forEach((name, i) => { MONTH_INDEX[name] = i; MONTH_INDEX[name.slice(0, 3)] = i; });

// Excel serial day (1900 system) for a calendar date — built from the parts,
// so no time zone moves the day.
const serial = (y, mIdx, d, hh = 0, mm = 0) => {
  const ms = Date.UTC(y, mIdx, d, hh, mm) - Date.UTC(1899, 11, 30);
  return ms / 86400000;
};

const validDay = (y, mIdx, d) => {
  const t = new Date(Date.UTC(y, mIdx, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mIdx && t.getUTCDate() === d;
};

/** A display-date string → { v: serial, z: format }; null when it is not one. */
const parseDateText = (s) => {
  let m = s.match(/^(\d{1,2}) ([A-Za-z]+) (\d{4})$/);
  if (m) {
    const mIdx = MONTH_INDEX[m[2].toLowerCase()];
    const y = Number(m[3]); const d = Number(m[1]);
    return mIdx !== undefined && validDay(y, mIdx, d) ? { v: serial(y, mIdx, d), z: DATE_FMT } : null;
  }
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]); const mIdx = Number(m[2]) - 1; const d = Number(m[3]);
    return validDay(y, mIdx, d) ? { v: serial(y, mIdx, d), z: DATE_FMT } : null;
  }
  m = s.match(/^(\d{2})-([A-Za-z]{3})-(\d{4}) (\d{2}):(\d{2})$/);
  if (m) {
    const mIdx = MONTH_INDEX[m[2].toLowerCase()];
    const y = Number(m[3]); const d = Number(m[1]);
    return mIdx !== undefined && validDay(y, mIdx, d)
      ? { v: serial(y, mIdx, d, Number(m[4]), Number(m[5])), z: DATETIME_FMT } : null;
  }
  return null;
};

// Measures that always read with one decimal (hours, rates, averages), even
// when every value in this export happens to be whole.
const ONE_DECIMAL_HEADER = /hour|hrs|rate|%|average|avg/i;

// Columns whose numbers are labels, not quantities: no thousands separator.
const PLAIN_NUMBER_HEADER = /\b(year|yr|fy|rank|no\.?|#|id|code|ssic|uen|count of year)\b/i;

const decimalsOf = (n) => {
  if (Number.isInteger(n)) return 0;
  const s = String(n);
  const dot = s.indexOf('.');
  return dot < 0 ? 2 : Math.min(2, s.length - dot - 1);
};

const numberFormat = (header, decimals) => {
  const frac = decimals > 0 ? `.${'0'.repeat(decimals)}` : '';
  return PLAIN_NUMBER_HEADER.test(header) ? `0${frac}` : `#,##0${frac}`;
};

const textWidth = (v) => String(v).split('\n').reduce((w, line) => Math.max(w, line.length), 0);

/** Rows of plain objects → a styled worksheet. */
const buildSheet = (rows) => {
  const headers = [];
  rows.forEach((r) => Object.keys(r).forEach((k) => { if (!headers.includes(k)) headers.push(k); }));

  // One decimal count per numeric column, so the column reads evenly.
  const decimals = headers.map((h) => rows.reduce((max, r) => (
    typeof r[h] === 'number' && Number.isFinite(r[h]) ? Math.max(max, decimalsOf(r[h])) : max
  ), ONE_DECIMAL_HEADER.test(h) ? 1 : 0));

  const ws = {};
  // 長表頭換行，不把整欄撐寬。
  const widths = headers.map((h) => Math.max(MIN_WIDTH, Math.min(HEADER_MAX_WIDTH, textWidth(h) + 2)));
  headers.forEach((h, c) => {
    ws[XLSX.utils.encode_cell({ r: 0, c })] = { t: 's', v: h, s: HEADER_STYLE };
  });

  rows.forEach((row, i) => {
    headers.forEach((h, c) => {
      const raw = row[h];
      const ref = XLSX.utils.encode_cell({ r: i + 1, c });
      const style = { alignment: BODY_ALIGN, border: BORDERS };
      let cell;
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        const z = numberFormat(h, decimals[c]);
        cell = { t: 'n', v: raw, z, s: { ...style, numFmt: z } };
        widths[c] = Math.max(widths[c], Math.min(MAX_WIDTH, String(Math.round(raw)).length + 4));
      } else if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        const v = serial(raw.getFullYear(), raw.getMonth(), raw.getDate());
        cell = { t: 'n', v, z: DATE_FMT, s: { ...style, numFmt: DATE_FMT } };
        widths[c] = Math.max(widths[c], 13);
      } else if (typeof raw === 'boolean') {
        cell = { t: 's', v: raw ? 'Yes' : 'No', s: style };
      } else {
        const text = raw === null || raw === undefined ? '' : String(raw);
        const date = text ? parseDateText(text.trim()) : null;
        if (date) {
          cell = { t: 'n', v: date.v, z: date.z, s: { ...style, numFmt: date.z } };
          widths[c] = Math.max(widths[c], date.z === DATE_FMT ? 13 : 18);
        } else {
          cell = { t: 's', v: text, s: style };
          widths[c] = Math.max(widths[c], Math.min(MAX_WIDTH, textWidth(text) + 2));
        }
      }
      ws[ref] = cell;
    });
  });

  const lastRow = rows.length;
  const lastCol = Math.max(0, headers.length - 1);
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: lastCol } });
  ws['!cols'] = widths.map((wch) => ({ wch }));
  ws['!rows'] = [{ hpt: 30 }];
  if (headers.length > 0) ws['!autofilter'] = { ref: ws['!ref'] };
  return ws;
};

/**
 * Export an array of plain objects to an .xlsx file and trigger browser download.
 * @param {Array<Object>} rows   - Array of flat objects (keys become column headers)
 * @param {string}        filename - e.g. "Late_Accounts_Report.xlsx" (dated on save)
 */
export function exportToExcel(rows, filename) {
  if (!rows || rows.length === 0) return;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildSheet(rows), 'Report');
  XLSX.writeFile(wb, datedFilename(filename));
}

/**
 * Export several sheets into one workbook — used where a report has a summary
 * and its supporting detail (e.g. the Official Training Report: one sheet per
 * official position level, one row per staff member).
 * @param {Array<{name: string, rows: Array<Object>}>} sheets - sheet name is
 *        truncated to Excel's 31-character limit; empty sheets are skipped.
 * @param {string} filename
 */
export function exportSheetsToExcel(sheets, filename) {
  const usable = (sheets || []).filter((s) => s.rows && s.rows.length > 0);
  if (usable.length === 0) return;
  const wb = XLSX.utils.book_new();
  usable.forEach((sheet) => {
    // Excel refuses : \ / ? * [ ] (full-width colon included) in a sheet name
    // and opens the file as corrupted; names are capped at 31 characters.
    const sheetName = String(sheet.name).replace(/[:：\\/?*[\]]/g, ' ').trim().slice(0, 31) || 'Sheet';
    XLSX.utils.book_append_sheet(wb, buildSheet(sheet.rows), sheetName);
  });
  XLSX.writeFile(wb, datedFilename(filename));
}
